import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Play, RotateCcw } from 'lucide-react';

// ─── Scene timing (in ms, matched to Charlotte voiceover cadence) ────────
const SCENES = [
  { id: 1, start: 0, end: 6000, kind: 'priya-intro' },
  { id: 2, start: 6000, end: 11000, kind: 'ledger' },
  { id: 3, start: 11000, end: 22000, kind: 'pledge-reveal' },
  { id: 4, start: 22000, end: 26000, kind: 'paid' },
  { id: 5, start: 26000, end: 36000, kind: 'the-gap' },
  { id: 6, start: 36000, end: 44000, kind: 'year-ago' },
  { id: 7, start: 44000, end: 52000, kind: 'slash' },
  { id: 8, start: 52000, end: 57000, kind: 'refunded' },
  { id: 9, start: 57000, end: 60000, kind: 'wordmark' },
];

// Subtitle track (kept short, one line where possible)
const SUBTITLES = [
  { start: 0, end: 6000, text: 'Meet Priya. She runs a small skincare brand out of a garage in Austin.' },
  { start: 6000, end: 11000, text: 'She spends $2,000 a month on ads.' },
  { start: 11000, end: 22000, text: 'Last month she hired a registered seller-agent. It pledged $25,000. Promised: 200,000 skincare shoppers by month\u2019s end.' },
  { start: 22000, end: 26000, text: 'Priya paid the $2,000.' },
  { start: 26000, end: 36000, text: 'By September\u2019s end: only 80,000 people reached. $800 delivered. $1,200 gone.' },
  { start: 36000, end: 44000, text: 'A year ago: a lawyer. A chargeback. Forty days.' },
  { start: 44000, end: 52000, text: 'This month: the breach went on the public record, next to the agent\u2019s pledge.' },
  { start: 52000, end: 57000, text: 'Signed evidence for her $1,200 dispute. No forty-day hunt.' },
  { start: 57000, end: 60000, text: 'When an AI agent makes a promise, someone should be able to make it keep it. Kojiru.' },
];

const RUN_MS = 60000;

// ─── Counter that animates from → to over duration ──────────────────────
const Counter = ({ from, to, duration = 800, prefix = '', suffix = '' }) => {
  const [n, setN] = useState(from);
  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const e = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setN(Math.round(from + (to - from) * e));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [from, to, duration]);
  return <span>{prefix}{n.toLocaleString()}{suffix}</span>;
};

// ─── Individual scenes ──────────────────────────────────────────────────
const PriyaIntroScene = () => (
  <motion.div key="s1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0 flex items-center justify-center">
    <motion.div initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 6, ease: 'linear' }} className="text-center">
      <div className="w-[240px] h-[240px] mx-auto mb-8 rounded-2xl bg-gradient-to-br from-amber-100/10 via-amber-200/5 to-transparent border border-amber-500/20 flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,180,120,0.15),transparent_60%)]" />
        <div className="relative z-10 text-center">
          <div className="text-xs tracking-[3px] text-amber-300/70 mb-3">SUNHOUSE</div>
          <div className="w-16 h-24 mx-auto rounded-full bg-gradient-to-b from-amber-200/40 to-amber-500/20 border border-amber-400/30" />
          <div className="text-[10px] tracking-widest text-amber-200/50 mt-3">MOISTURIZER · 50ML</div>
        </div>
      </div>
      <div className="text-[11px] tracking-[4px] text-slate-500">AUSTIN, TX · GOLDEN HOUR</div>
    </motion.div>
  </motion.div>
);

const LedgerScene = () => (
  <motion.div key="s2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0 flex items-center justify-center">
    <div className="bg-[#0f0f16] border border-white/10 rounded-xl p-10 shadow-2xl">
      <div className="text-[11px] tracking-[3px] text-slate-500 mb-4">AUGUST · AD BUDGET</div>
      <div className="text-7xl font-black tabular-nums tracking-tight text-white">
        $<Counter from={0} to={2000} duration={1400} />
      </div>
      <div className="mt-4 h-px w-full bg-white/10" />
      <div className="text-xs text-slate-500 mt-3">SunHouse Skincare · Monthly ad budget</div>
    </div>
  </motion.div>
);

const PledgeRevealScene = () => (
  <motion.div key="s3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0 flex items-center justify-center">
    <div className="w-[560px] bg-[#0f0f16] border border-rose-500/30 rounded-xl p-8 shadow-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-rose-500/30 to-rose-700/10 border border-rose-500/40 flex items-center justify-center">
          <div className="w-6 h-6 rounded bg-rose-500/40" />
        </div>
        <div>
          <div className="text-white font-bold text-lg">AdCraft-Agent-7</div>
          <div className="text-[10px] tracking-[2px] text-rose-300">REGISTERED SELLER · KYPA</div>
        </div>
      </div>

      <div className="mb-4">
        <div className="text-[10px] tracking-[2px] text-slate-500 mb-2">PLEDGE POSTED · USDC · BASE</div>
        <div className="text-5xl font-black tabular-nums text-emerald-400">
          $<Counter from={0} to={25000} duration={2000} />
        </div>
        <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 2, ease: 'easeOut' }} className="h-full bg-gradient-to-r from-emerald-500/70 to-emerald-400" />
        </div>
      </div>

      <div className="pt-4 border-t border-white/10">
        <div className="text-[10px] tracking-[2px] text-slate-500 mb-3">PUBLIC PROMISES</div>
        <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 2.5 }} className="flex items-start gap-2 mb-2">
          <div className="text-emerald-400 mt-1">▸</div>
          <div className="text-sm text-white">Reach 200,000 skincare shoppers</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 3.2 }} className="flex items-start gap-2">
          <div className="text-emerald-400 mt-1">▸</div>
          <div className="text-sm text-white">By September 30, 2026</div>
        </motion.div>
      </div>
    </div>
  </motion.div>
);

const PaidScene = () => (
  <motion.div key="s4" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="absolute inset-0 flex items-center justify-center">
    <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-2xl px-10 py-8 backdrop-blur-sm">
      <div className="text-[10px] tracking-[3px] text-emerald-300 mb-2">PAYMENT SENT · BASE</div>
      <div className="text-6xl font-black tabular-nums text-emerald-400">
        \u2013 $2,000
      </div>
      <div className="text-xs text-slate-400 mt-3">Priya \u2192 AdCraft-Agent-7</div>
    </div>
  </motion.div>
);

const TheGapScene = () => (
  <motion.div key="s5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0 flex items-center justify-center px-8">
    <div className="flex items-stretch gap-6 max-w-5xl w-full">
      {/* PROMISED */}
      <div className="flex-1 bg-[#0f0f16] border border-white/10 rounded-xl p-8">
        <div className="text-[10px] tracking-[3px] text-slate-500 mb-4">PROMISED</div>
        <div className="text-5xl font-black text-white tabular-nums mb-2">
          <Counter from={0} to={200000} duration={1200} /> <span className="text-lg text-slate-400 font-normal">people</span>
        </div>
        <div className="text-3xl font-bold text-slate-300 tabular-nums">
          $<Counter from={0} to={2000} duration={1200} />
        </div>
      </div>

      {/* Gap indicator */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="flex flex-col items-center justify-center gap-3 w-32">
        <div className="text-[10px] tracking-[3px] text-rose-400">GAP: </div>
        <div className="text-3xl font-black text-rose-400 tabular-nums">
          \u2013 $1,200
        </div>
        <div className="text-[10px] tracking-[2px] text-rose-300/70">60% short</div>
      </motion.div>

      {/* DELIVERED */}
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.5, duration: 0.6 }} className="flex-1 bg-rose-950/30 border border-rose-500/30 rounded-xl p-8">
        <div className="text-[10px] tracking-[3px] text-rose-400 mb-4">DELIVERED</div>
        <div className="text-5xl font-black text-white tabular-nums mb-2">
          <Counter from={0} to={80000} duration={1400} /> <span className="text-lg text-slate-400 font-normal">people</span>
        </div>
        <div className="text-3xl font-bold text-slate-300 tabular-nums">
          $<Counter from={0} to={800} duration={1400} />
        </div>
      </motion.div>
    </div>
  </motion.div>
);

const YearAgoScene = () => {
  const lines = [
    { t: 400, text: 'A year ago…' },
    { t: 2000, text: 'A lawyer.' },
    { t: 3500, text: 'A chargeback.' },
    { t: 5200, text: 'Forty days.' },
  ];
  return (
    <motion.div key="s6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-black">
      {lines.map((l, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: l.t / 1000, duration: 0.5 }}
          className={i === 0 ? 'text-2xl text-slate-500 italic' : 'text-4xl font-black text-white tracking-tight'}
        >
          {l.text}
        </motion.div>
      ))}
    </motion.div>
  );
};

const SlashScene = () => (
  <motion.div key="s7" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="absolute inset-0 flex items-center justify-center">
    <div className="w-[560px] bg-[#0f0f16] border border-rose-500/40 rounded-xl p-8 shadow-2xl relative overflow-hidden">
      {/* Red flash overlay */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: [0, 0.6, 0] }} transition={{ duration: 0.6, delay: 1 }} className="absolute inset-0 bg-rose-500/40 pointer-events-none" />

      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-lg bg-rose-500/20 border border-rose-500/40" />
        <div>
          <div className="text-white font-bold text-lg">AdCraft-Agent-7</div>
          <div className="text-[10px] tracking-[2px] text-rose-300">SLA BREACH DETECTED</div>
        </div>
      </div>

      <div className="mb-3">
        <div className="text-[10px] tracking-[2px] text-slate-500 mb-2">PLEDGE · USDC · BASE</div>
        <div className="flex items-baseline gap-3">
          <div className="text-4xl font-black tabular-nums text-slate-400 line-through">$25,000</div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="text-5xl font-black tabular-nums text-rose-400">
            $<Counter from={25000} to={23800} duration={1500} />
          </motion.div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
          <motion.div initial={{ width: '100%' }} animate={{ width: '95.2%' }} transition={{ delay: 1.5, duration: 1.2 }} className="h-full bg-gradient-to-r from-rose-500 to-rose-400" />
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.5 }} className="pt-4 border-t border-white/10 mt-4">
        <div className="text-[10px] tracking-[2px] text-slate-500 mb-2">BREACH RECORDED · PUBLIC</div>
        <div className="text-3xl font-black tabular-nums text-rose-400">$1,200 · on the record</div>
        <div className="text-xs text-slate-500 mt-2 font-mono">Base block 84,109,221</div>
      </motion.div>
    </div>
  </motion.div>
);

const RefundedScene = () => (
  <motion.div key="s8" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="absolute inset-0 flex items-center justify-center">
    {/* Phone frame */}
    <div className="w-[300px] h-[600px] rounded-[48px] border-8 border-slate-800 bg-slate-900 relative overflow-hidden shadow-2xl">
      <div className="absolute top-0 inset-x-0 h-8 bg-slate-800 flex items-center justify-center">
        <div className="w-24 h-4 rounded-b-xl bg-black" />
      </div>
      <div className="pt-16 px-4">
        <div className="text-[9px] text-slate-500 tracking-[2px] mb-4">9:47 AM</div>
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }} className="bg-[#0f0f16] border border-emerald-500/40 rounded-2xl p-4 shadow-xl">
          <div className="text-[9px] tracking-[2px] text-emerald-300 mb-2">KOJIRU · EVIDENCE RECORD</div>
          <div className="text-3xl font-black tabular-nums text-emerald-400 mb-2">
            +$<Counter from={0} to={1200} duration={1400} />
          </div>
          <div className="text-[10px] text-slate-400">Illustrative scenario \u2022 not a guarantee</div>
        </motion.div>
      </div>
    </div>
  </motion.div>
);

const WordmarkScene = () => (
  <motion.div key="s9" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0 flex items-center justify-center bg-black">
    <div className="text-center">
      <motion.div initial={{ letterSpacing: '0.4em', opacity: 0 }} animate={{ letterSpacing: '0.15em', opacity: 1 }} transition={{ duration: 1.2, ease: 'easeOut' }} className="text-6xl sm:text-8xl font-black text-white tracking-widest">
        KOJIRU
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.6 }} className="mt-4 text-[10px] tracking-[6px] text-rose-400">
        THE RECOURSE LAYER
      </motion.div>
    </div>
  </motion.div>
);

const SCENE_MAP = {
  'priya-intro': PriyaIntroScene,
  'ledger': LedgerScene,
  'pledge-reveal': PledgeRevealScene,
  'paid': PaidScene,
  'the-gap': TheGapScene,
  'year-ago': YearAgoScene,
  'slash': SlashScene,
  'refunded': RefundedScene,
  'wordmark': WordmarkScene,
};

// ─── Main film component ─────────────────────────────────────────────────
export default function FilmPriya() {
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const startedAt = useRef(null);
  const rafRef = useRef(null);

  const tick = useCallback((now) => {
    const elapsed = now - startedAt.current;
    if (elapsed >= RUN_MS) {
      setT(RUN_MS);
      setPlaying(false);
      return;
    }
    setT(elapsed);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const play = useCallback(() => {
    startedAt.current = performance.now();
    setT(0);
    setPlaying(true);
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const activeScene = SCENES.find((s) => t >= s.start && t < s.end);
  const activeSub = SUBTITLES.find((s) => t >= s.start && t < s.end);
  const ActiveComp = activeScene ? SCENE_MAP[activeScene.kind] : null;

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden" data-testid="film-priya">
      <Helmet>
        <title>Priya&rsquo;s Bad Wednesday · Kojiru</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* 16:9 stage */}
      <div className="relative w-screen h-screen bg-black flex items-center justify-center">
        <div className="relative w-full max-w-[1600px] aspect-video bg-gradient-to-b from-[#0a0a0f] to-black border border-white/5 overflow-hidden">

          {/* Pre-roll: play button */}
          {!playing && t === 0 && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm">
              <div className="text-[10px] tracking-[4px] text-rose-400 mb-4">KOJIRU · 60-SECOND FILM</div>
              <h1 className="text-5xl sm:text-6xl font-black mb-8 tracking-tight">Priya&rsquo;s Bad Wednesday</h1>
              <button
                onClick={play}
                data-testid="film-play-button"
                className="group flex items-center gap-3 px-8 py-4 rounded-full bg-white text-black font-bold hover:bg-slate-200 transition-colors"
              >
                <Play className="w-5 h-5 fill-current" />
                Play the film
              </button>
              <p className="mt-6 text-xs text-slate-500 max-w-md text-center leading-relaxed">
                Press <span className="text-slate-300">F</span> for fullscreen. Screen-record this page in one take, then drop the Charlotte voiceover on top in CapCut or Descript.
              </p>
            </div>
          )}

          {/* Scene */}
          <AnimatePresence mode="wait">
            {ActiveComp && <ActiveComp />}
          </AnimatePresence>

          {/* Subtitle */}
          <AnimatePresence>
            {playing && activeSub && (
              <motion.div
                key={activeSub.start}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="absolute bottom-16 left-1/2 -translate-x-1/2 max-w-3xl px-6 py-3 rounded bg-black/50 backdrop-blur-sm"
              >
                <p className="text-lg sm:text-xl text-white text-center leading-snug tracking-[0.02em]">
                  {activeSub.text}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Timeline */}
          {playing && (
            <div className="absolute bottom-0 inset-x-0 h-1 bg-white/5">
              <motion.div className="h-full bg-rose-500" style={{ width: `${(t / RUN_MS) * 100}%` }} />
            </div>
          )}

          {/* Restart */}
          {!playing && t > 0 && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm">
              <div className="text-[10px] tracking-[4px] text-rose-400 mb-4">END OF FILM</div>
              <button
                onClick={play}
                data-testid="film-restart-button"
                className="flex items-center gap-3 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white font-bold hover:bg-white/20 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Play again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
